import React, { useMemo } from 'react';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import * as DropdownMenu from '@/components/DropdownMenu/DropdownMenu';
import DropdownMenuItem from '@/components/DropdownMenu/DropdownMenuItem';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';

const OPTIONS = [
  { id: 'newest', labelKey: 'pages.bookmarks.newest', fallback: 'Newest', icon: 'arrow-down-wide-short' },
  { id: 'oldest', labelKey: 'pages.bookmarks.oldest', fallback: 'Oldest', icon: 'arrow-up-wide-short' },
];

/**
 * Sort dropdown for saved bookmarks (newest / oldest).
 * Trigger chrome matches /agents search field: 40px tall, 10px radius,
 * 1px border, solid_color_block fill — no accent_color_primary.
 */
function BookmarksSortDropdown({ value = 'newest', onChange }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS } = useResponsiveness();

  const active = OPTIONS.find(o => o.id === value) || OPTIONS[0];
  const activeTitle = t(active.labelKey) || active.fallback;

  const styles = useMemo(() => StyleSheet.create({
    trigger: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 12,
      height: 40,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      ...(Platform.OS === 'web' && { cursor: 'pointer' }),
    },
    triggerHovered: {
      backgroundColor: colors.solid_color_secondary,
    },
  }), [colors]);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Pressable
          style={({ hovered }) => [
            styles.trigger,
            hovered && styles.triggerHovered,
          ]}
          accessibilityRole="button"
          accessibilityLabel={activeTitle}
        >
          <Icon name={active.icon} size={14} color={colors.text_color_secondary} />
          {!isXS && (
            <View style={{ maxWidth: 120 }}>
              <Text size="s" weight="medium" color="primary" numberOfLines={1}>
                {activeTitle}
              </Text>
            </View>
          )}
          <Icon name="chevron-down" size={10} color={colors.text_color_tertiary} />
        </Pressable>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className={Platform.OS === 'web' ? 'DropdownMenuContent' : undefined}
        align="end"
        sideOffset={5}
        style={{ minWidth: 200 }}
      >
        {OPTIONS.map(opt => (
          Platform.OS === 'web' ? (
            <DropdownMenuItem
              key={opt.id}
              icon={opt.icon}
              title={t(opt.labelKey) || opt.fallback}
              isActive={value === opt.id}
              onSelect={() => onChange?.(opt.id)}
            />
          ) : (
            // Native menus only read children whose element type is
            // DropdownMenu.Item, so the wrapper is web-only here.
            <DropdownMenu.Item key={opt.id} onSelect={() => onChange?.(opt.id)}>
              {value === opt.id && <DropdownMenu.ItemIcon ios={{ name: 'checkmark' }} />}
              <DropdownMenu.ItemTitle>{t(opt.labelKey) || opt.fallback}</DropdownMenu.ItemTitle>
            </DropdownMenu.Item>
          )
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

export default React.memo(BookmarksSortDropdown);
